import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle, Clock, Upload, Download, FileText, File, Image, Video, Loader, X, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PartnerCaseDetailPage = () => {
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [caseData, setCaseData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    // Check if user has access token
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/partner');
      return;
    }
    loadCaseData();
    loadRequests();
    loadCaseDocuments();
  }, [caseId]);

  const loadCaseData = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCaseData(data);
    } catch (error) {
      console.error('Load case failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/cases/${caseId}/requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Load requests failed:', error);
    }
  };

  const loadCaseDocuments = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-documents/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCaseDocuments(data.documents || []);
    } catch (error) {
      console.error('Load case documents failed:', error);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 25MB)');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Dateityp nicht erlaubt. Erlaubt: PDF, JPG, PNG, HEIC, MP4');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('partner_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('case_id', caseId);
      formData.append('document_type', 'partner_upload');
      formData.append('visibility', 'partner_admin');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Dokument hochgeladen');
        await loadCaseDocuments();
        setShowUploadModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload fehlgeschlagen');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Verbindungsfehler beim Upload');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    if (mimeType?.startsWith('video/')) return Video;
    if (mimeType === 'application/pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const handleAnswerRequest = async (requestId) => {
    if (!answerText.trim()) {
      alert('Bitte geben Sie eine Antwort ein');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('partner_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/cases/${caseId}/requests/${requestId}/answer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answer_message: answerText })
      });
      setAnswerText('');
      await loadRequests();
      await loadCaseData();
      alert('Antwort gesendet');
    } catch (error) {
      alert('Fehler beim Senden der Antwort');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-700',
      eingegangen: 'bg-blue-100 text-blue-700',
      rueckfrage_offen: 'bg-orange-100 text-orange-700',
      termin_vereinbart: 'bg-purple-100 text-purple-700',
      begutachtung_durchgefuehrt: 'bg-indigo-100 text-indigo-700',
      gutachten_in_erstellung: 'bg-yellow-100 text-yellow-700',
      gutachten_fertig: 'bg-teal-100 text-teal-700',
      abgeschlossen: 'bg-brand-green/10 text-brand-green'
    };
    return badges[status] || 'bg-gray-100 text-gray-600';
  };

  const getRequestStatusBadge = (status) => {
    const badges = {
      open: { class: 'bg-orange-100 text-orange-700', icon: AlertCircle, text: 'Offen' },
      answered: { class: 'bg-blue-100 text-blue-700', icon: CheckCircle, text: 'Beantwortet' },
      closed: { class: 'bg-gray-100 text-gray-700', icon: CheckCircle, text: 'Geschlossen' }
    };
    return badges[status] || badges.open;
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Laden...</div>;
  }

  if (!caseData) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Fall nicht gefunden</div>;
  }

  const TABS = [
    { key: 'overview', label: 'Überblick' },
    { key: 'bilder', label: 'Bilder' },
    { key: 'dokumente', label: 'Dokumente' },
    { key: 'requests', label: `Rückfragen ${requests.filter(r => r.status === 'open').length > 0 ? `(${requests.filter(r => r.status === 'open').length})` : ''}` },
    { key: 'gutachten', label: 'Gutachten' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/portal/dashboard')} className="text-white hover:text-brand-green">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-white">Fall: {caseData.license_plate || caseData.id.slice(0,8)}</h1>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadge(caseData.status)}`}>
            {caseData.status}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white border border-gray-200 rounded-2xl mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? 'text-brand-green border-b-2 border-brand-green bg-brand-green/5'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-8">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Auftragsdaten</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-semibold">Kennzeichen:</span> {caseData.license_plate || '-'}</div>
                      <div><span className="font-semibold">Schadentag:</span> {caseData.damage_date || '-'}</div>
                      <div><span className="font-semibold">Schadenort:</span> {caseData.damage_location || '-'}</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Fahrzeugdaten</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-semibold">Fahrzeug:</span> {caseData.vehicle_manufacturer} {caseData.vehicle_model}</div>
                      <div><span className="font-semibold">KM-Stand:</span> {caseData.vehicle_mileage} km</div>
                      <div><span className="font-semibold">Schadenzone:</span> {caseData.damage_zone}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'bilder' && (
              <div className="grid md:grid-cols-3 gap-6">
                {caseData.images?.map((img, idx) => {
                  // Handle both string URLs and image objects
                  const imgUrl = typeof img === 'string' ? img : img.url || img.image_url;
                  const fullUrl = imgUrl?.startsWith('http') ? imgUrl : `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
                  
                  return (
                    <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                      <img
                        src={fullUrl}
                        alt={`Bild ${idx + 1}`}
                        className="w-full h-48 object-cover cursor-pointer hover:opacity-90"
                        onClick={() => window.open(fullUrl, '_blank')}
                        onError={(e) => {
                          console.error('Image load failed:', imgUrl);
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23f3f4f6" width="400" height="300"/><text x="50%" y="50%" text-anchor="middle" fill="%239ca3af" font-size="16">Bild nicht verfügbar</text></svg>';
                        }}
                      />
                    </div>
                  );
                })}
                {(!caseData.images || caseData.images.length === 0) && (
                  <p className="col-span-3 text-center text-gray-500 py-8">Keine Bilder hochgeladen</p>
                )}
              </div>
            )}

            {activeTab === 'dokumente' && (
              <div className="space-y-6">
                {/* Upload Section */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Dokumente</h3>
                    <p className="text-sm text-gray-500">Laden Sie hier zusätzliche Dokumente für diesen Fall hoch</p>
                  </div>
                  <Button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-brand-green hover:bg-brand-green-dark text-white"
                    data-testid="upload-document-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Dokument hochladen
                  </Button>
                </div>

                {/* Upload Modal */}
                {showUploadModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">Dokument hochladen</h3>
                        <button 
                          onClick={() => setShowUploadModal(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand-green transition-colors">
                        <input
                          type="file"
                          id="doc-upload"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.heic,.mp4"
                          onChange={handleDocumentUpload}
                          disabled={uploading}
                        />
                        <label htmlFor="doc-upload" className="cursor-pointer">
                          {uploading ? (
                            <>
                              <Loader className="w-12 h-12 text-brand-green mx-auto mb-3 animate-spin" />
                              <p className="text-gray-600">Wird hochgeladen...</p>
                            </>
                          ) : (
                            <>
                              <Plus className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-700 font-medium mb-1">Datei auswählen</p>
                              <p className="text-sm text-gray-500">PDF, JPG, PNG, HEIC, MP4 (max. 25MB)</p>
                            </>
                          )}
                        </label>
                      </div>
                      
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-700">
                          <strong>Hinweis:</strong> Hochgeladene Dokumente sind für Sie und den Admin sichtbar.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents List */}
                {caseDocuments.length === 0 && (!caseData.documents || caseData.documents.length === 0) ? (
                  <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">Keine Dokumente hochgeladen</p>
                    <Button
                      onClick={() => setShowUploadModal(true)}
                      variant="outline"
                      className="border-brand-green text-brand-green hover:bg-brand-green/5"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Erstes Dokument hochladen
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Case Documents from new collection */}
                    {caseDocuments.map((doc) => {
                      const DocIcon = getDocumentIcon(doc.mime_type);
                      return (
                        <div 
                          key={doc.id} 
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center">
                              <DocIcon className="w-5 h-5 text-brand-green" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 truncate max-w-[200px]">
                                {doc.original_filename || 'Dokument'}
                              </p>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span>{formatFileSize(doc.file_size)}</span>
                                <span>•</span>
                                <span>{formatDate(doc.created_at)}</span>
                                {doc.uploaded_by_name && (
                                  <>
                                    <span>•</span>
                                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                      {doc.uploaded_by_role === 'admin' ? 'Admin' : 'Partner'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/case-documents/download/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-brand-green hover:bg-gray-50 font-medium text-sm transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        </div>
                      );
                    })}
                    
                    {/* Legacy documents from case.documents */}
                    {caseData.documents?.map((doc, idx) => (
                      <div key={`legacy-${idx}`} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Dokument {idx + 1}</p>
                            <p className="text-sm text-gray-500">Älteres Dokument</p>
                          </div>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}${doc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-brand-green hover:bg-gray-50 font-medium text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'requests' && (
              <div className="space-y-6">
                {requests.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-brand-green mx-auto mb-4" />
                    <p className="text-gray-600">Keine Rückfragen vorhanden</p>
                  </div>
                ) : (
                  requests.map((req) => {
                    const statusBadge = getRequestStatusBadge(req.status);
                    const StatusIcon = statusBadge.icon;

                    return (
                      <div key={req.request_id} className="border border-gray-200 rounded-xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 mb-2">{req.title}</h3>
                            <p className="text-sm text-gray-600 mb-4">{req.message}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${statusBadge.class}`}>
                            <StatusIcon className="w-4 h-4" />
                            {statusBadge.text}
                          </span>
                        </div>

                        {req.required_upload_slots.length > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                            <p className="text-sm font-semibold text-yellow-800 mb-2">Erforderliche Bilder:</p>
                            <ul className="text-sm text-yellow-700 list-disc list-inside">
                              {req.required_upload_slots.map(slot => <li key={slot}>{slot}</li>)}
                            </ul>
                          </div>
                        )}

                        {req.required_documents.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <p className="text-sm font-semibold text-blue-800 mb-2">Erforderliche Dokumente:</p>
                            <ul className="text-sm text-blue-700 list-disc list-inside">
                              {req.required_documents.map(doc => <li key={doc}>{doc}</li>)}
                            </ul>
                          </div>
                        )}

                        {req.status === 'open' && (
                          <div className="border-t border-gray-200 pt-4 mt-4">
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Antwort an Admin</label>
                            <textarea
                              value={answerText}
                              onChange={(e) => setAnswerText(e.target.value)}
                              placeholder="Optional: Nachricht an den Admin..."
                              rows={3}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green resize-none mb-3"
                            />
                            <Button
                              onClick={() => handleAnswerRequest(req.request_id)}
                              disabled={submitting}
                              className="bg-brand-green hover:bg-brand-green-dark text-white"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              {submitting ? 'Senden...' : 'Nachreichung senden'}
                            </Button>
                          </div>
                        )}

                        {req.status === 'answered' && req.answer_message && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                            <p className="text-sm font-semibold text-green-800 mb-1">Ihre Antwort:</p>
                            <p className="text-sm text-green-700">{req.answer_message}</p>
                            <p className="text-xs text-green-600 mt-2">
                              Beantwortet am: {new Date(req.answered_at).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'gutachten' && (
              <div className="space-y-6">
                {(() => {
                  const report = caseData.report;
                  const hasReport = report && report.versions && report.versions.length > 0;
                  const isVisible = hasReport && report.visible_to_partner === true;

                  if (!hasReport || !isVisible) {
                    return (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">Gutachten wird aktuell erstellt.</p>
                      </div>
                    );
                  }

                  const currentVersion = report.versions.find(v => v.version === report.current_version);
                  
                  return (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-bold text-gray-900 mb-2">Gutachten verfügbar</h3>
                          <p className="text-sm text-gray-600">
                            Version {report.current_version} vom {new Date(currentVersion?.uploaded_at || '').toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      
                      <Button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('partner_token');
                            const response = await fetch(
                              `${process.env.REACT_APP_BACKEND_URL}/api/partner/cases/${caseId}/report/download`,
                              { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            
                            if (response.status === 403) {
                              alert('Gutachten ist aktuell nicht verfügbar.');
                              return;
                            }
                            
                            if (!response.ok) {
                              throw new Error('Download fehlgeschlagen');
                            }
                            
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = currentVersion?.filename || 'gutachten.pdf';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (error) {
                            console.error('Download error:', error);
                            alert('Fehler beim Herunterladen');
                          }
                        }}
                        className="bg-brand-green hover:bg-brand-green-dark text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Gutachten herunterladen
                      </Button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerCaseDetailPage;
